import { lazy } from 'react';
const defaultSleep = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms));
/**
 * Retries an async factory (typically a dynamic `import()`) with exponential
 * backoff. Mitigates transient failures: intermittent network, constrained
 * devices, or stale deployments that are being rolled forward.
 *
 * The UI library stays free of logging concerns; callers can subscribe to
 * `onRetry`, `onSuccessAfterRetry`, and `onFinalFailure` to emit telemetry.
 */ export const retryImport = async (factory, options = {})=>{
    const { maxRetries = 5, baseDelayMs = 200, sleep = defaultSleep, onRetry, onSuccessAfterRetry, onFinalFailure } = options;
    const maxAttempts = maxRetries + 1;
    // Delay to wait BEFORE the given 1-indexed attempt. Attempts 1 and 2 run
    // immediately (initial load + first retry); each later attempt doubles.
    const getDelayBeforeAttempt = (nextAttempt)=>{
        if (nextAttempt <= 2) {
            return 0;
        }
        return baseDelayMs * 2 ** (nextAttempt - 3);
    };
    let lastError;
    for(let attempt = 1; attempt <= maxAttempts; attempt++){
        try {
            const loaded = await factory();
            if (attempt > 1) {
                onSuccessAfterRetry?.({
                    attempts: attempt
                });
            }
            return loaded;
        } catch (error) {
            lastError = error;
            const nextAttempt = attempt + 1;
            if (nextAttempt > maxAttempts) {
                break;
            }
            const delayMs = getDelayBeforeAttempt(nextAttempt);
            onRetry?.({
                attempt,
                error,
                delayMs
            });
            if (delayMs > 0) {
                await sleep(delayMs);
            }
        }
    }
    onFinalFailure?.({
        attempts: maxAttempts,
        error: lastError
    });
    throw lastError;
};
/**
 * Wraps `React.lazy` with {@link retryImport} so transient dynamic-import
 * failures are retried before the component throws.
 */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = (factory, options = {})=>lazy(()=>retryImport(factory, options));

//# sourceMappingURL=lazyWithRetry.js.map